/**
 * Notes Tracker
 * Manages personal notes and tags for frameworks
 */

export interface FrameworkNote {
  id: string;
  frameworkId: string;
  frameworkName: string;
  content: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  isPinned: boolean;
}

class NotesTracker {
  private notes: FrameworkNote[] = [];
  private readonly STORAGE_KEY = 'peakPriorities_frameworkNotes';

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Load notes from LocalStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.notes = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load notes from storage:', error);
      this.notes = [];
    }
  }

  /**
   * Save notes to LocalStorage
   */
  private saveToStorage(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.notes));
    } catch (error) {
      console.error('Failed to save notes to storage:', error);
    }
  }

  /**
   * Generate unique ID
   */
  private generateId(): string {
    return `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get note for a specific framework
   */
  getNote(frameworkId: string): FrameworkNote | null {
    return this.notes.find(n => n.frameworkId === frameworkId) || null;
  }

  /**
   * Get all notes
   */
  getAllNotes(): FrameworkNote[] {
    return [...this.notes].sort((a, b) => {
      // Pinned notes first
      if (a.isPinned !== b.isPinned) {
        return a.isPinned ? -1 : 1;
      }
      // Then by update time
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }

  /**
   * Get pinned notes
   */
  getPinnedNotes(): FrameworkNote[] {
    return this.notes.filter(n => n.isPinned);
  }

  /**
   * Create or update note
   */
  saveNote(data: {
    frameworkId: string;
    frameworkName: string;
    content: string;
    tags?: string[];
  }): FrameworkNote {
    const existing = this.notes.find(n => n.frameworkId === data.frameworkId);
    const now = new Date().toISOString();

    if (existing) {
      // Update existing note
      existing.content = data.content;
      existing.tags = data.tags || existing.tags;
      existing.updatedAt = now;
      this.saveToStorage();
      return existing;
    } else {
      // Create new note
      const newNote: FrameworkNote = {
        id: this.generateId(),
        frameworkId: data.frameworkId,
        frameworkName: data.frameworkName,
        content: data.content,
        tags: data.tags || [],
        createdAt: now,
        updatedAt: now,
        isPinned: false,
      };
      this.notes.push(newNote);
      this.saveToStorage();
      return newNote;
    }
  }

  /**
   * Delete note
   */
  deleteNote(frameworkId: string): boolean {
    const index = this.notes.findIndex(n => n.frameworkId === frameworkId);
    if (index === -1) return false;

    this.notes.splice(index, 1);
    this.saveToStorage();
    return true;
  }

  /**
   * Toggle pin status
   */
  togglePin(frameworkId: string): boolean {
    const note = this.notes.find(n => n.frameworkId === frameworkId);
    if (!note) return false;

    note.isPinned = !note.isPinned;
    note.updatedAt = new Date().toISOString();
    this.saveToStorage();
    return note.isPinned;
  }

  /**
   * Add tag to note
   */
  addTag(frameworkId: string, tag: string): boolean {
    const note = this.notes.find(n => n.frameworkId === frameworkId);
    if (!note) return false;

    const normalizedTag = tag.trim().toLowerCase();
    if (!normalizedTag || note.tags.includes(normalizedTag)) return false;

    note.tags.push(normalizedTag);
    note.updatedAt = new Date().toISOString();
    this.saveToStorage();
    return true;
  }

  /**
   * Remove tag from note
   */
  removeTag(frameworkId: string, tag: string): boolean {
    const note = this.notes.find(n => n.frameworkId === frameworkId);
    if (!note) return false;

    const index = note.tags.indexOf(tag);
    if (index === -1) return false;

    note.tags.splice(index, 1);
    note.updatedAt = new Date().toISOString();
    this.saveToStorage();
    return true;
  }

  /**
   * Search notes by content or tags
   */
  searchNotes(query: string): FrameworkNote[] {
    const lowerQuery = query.toLowerCase().trim();
    if (!lowerQuery) return this.getAllNotes();

    return this.notes.filter(note => {
      return (
        note.content.toLowerCase().includes(lowerQuery) ||
        note.frameworkName.toLowerCase().includes(lowerQuery) ||
        note.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
      );
    });
  }

  /**
   * Get notes by tag
   */
  getNotesByTag(tag: string): FrameworkNote[] {
    const normalizedTag = tag.toLowerCase().trim();
    return this.notes.filter(n => n.tags.includes(normalizedTag));
  }

  /**
   * Get all unique tags
   */
  getAllTags(): string[] {
    const tagSet = new Set<string>();
    this.notes.forEach(note => {
      note.tags.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }

  /**
   * Get stats
   */
  getStats() {
    return {
      totalNotes: this.notes.length,
      pinnedNotes: this.notes.filter(n => n.isPinned).length,
      totalTags: this.getAllTags().length,
      recentNotes: this.notes
        .filter(n => {
          const daysSinceUpdate = (Date.now() - new Date(n.updatedAt).getTime()) / (1000 * 60 * 60 * 24);
          return daysSinceUpdate <= 7;
        })
        .length,
    };
  }

  /**
   * Export notes as JSON
   */
  exportNotes(): string {
    return JSON.stringify(this.notes, null, 2);
  }

  /**
   * Import notes from JSON
   */
  importNotes(jsonString: string): boolean {
    try {
      const imported = JSON.parse(jsonString);
      if (!Array.isArray(imported)) return false;

      // Validate structure
      const valid = imported.every(note => 
        note.id && note.frameworkId && note.content && note.createdAt
      );
      if (!valid) return false;

      this.notes = imported;
      this.saveToStorage();
      return true;
    } catch (error) {
      console.error('Failed to import notes:', error);
      return false;
    }
  }

  /**
   * Clear all notes (with confirmation)
   */
  clearAllNotes(): void {
    this.notes = [];
    this.saveToStorage();
  }
}

// Singleton instance
export const notesTracker = new NotesTracker();

